// 기계 원리 — 기어·벨트·지렛대·도르래·빗면·유압과 운동 원리. 그림을 보고 방향·힘·회전수를 판단한다.
// 이상적인 경우(마찰·도르래 무게 무시)로 계산한다.
import type { ChoiceQuestion } from './ChoiceDrill';
import { int, makeRng, pick, shuffle, type Rng } from './rng';

export interface Gear { x: number; y: number; r: number; teeth: number; label: string }
export type MechQuestion = ChoiceQuestion & { ask: string; options: string[] } & (
  | { kind: 'gears'; gears: Gear[]; showTeeth: boolean; shafts: [number, number][]; driverCW: boolean }
  | { kind: 'belt'; ra: number; rb: number; crossed: boolean; showSize: boolean }
  | { kind: 'lever'; weights: { pos: number; kg: number | null }[] }
  | { kind: 'pulley'; ropes: number; kg: number }
  | { kind: 'incline'; weight: number; h: number; len: number }
  | { kind: 'hydraulic'; small: number; big: number; force: number }
  | { kind: 'concept' }
);

export const MECH_COUNT = 6;
const CW = '시계 방향', CCW = '시계 반대 방향', JAM = '돌 수 없다';
const fmt = (x: number) => x.toLocaleString('ko-KR', { maximumFractionDigits: 2 });

/** 숫자 보기 — 정답과 실수 값들, 겹치면 근처 값으로 채운다 */
function numOptions(r: Rng, right: number, mistakes: number[], unit: string): { options: string[]; answer: number } {
  const vals = [right];
  for (const v of [...mistakes, right * 2, right + 1, right * 1.5, right + 2, right + 3]) {
    if (vals.length >= 4) break;
    if (v > 0 && Number.isFinite(v) && !vals.some((x) => Math.abs(x - v) < 1e-9)) vals.push(v);
  }
  const options = shuffle(r, vals).map((v) => `${fmt(v)}${unit}`);
  return { options, answer: options.indexOf(`${fmt(right)}${unit}`) };
}
const dirOptions = (r: Rng, cw: boolean, jam = false) => {
  const options = jam ? [CW, CCW, JAM] : shuffle(r, [CW, CCW]);
  return { options, answer: options.indexOf(jam ? JAM : cw ? CW : CCW) };
};

/** 합집합의 갈래마다 limitSec 만 뺀 꼴 (그냥 Omit 은 갈래가 합쳐져 버린다) */
type Made = MechQuestion extends infer T ? (T extends unknown ? Omit<T, 'limitSec'> : never) : never;

// ---- 기어 ----
const TEETH = [10, 12, 15, 16, 20, 24, 30, 36, 40];
const radius = (teeth: number) => 8 + teeth * 0.55;

function gearRow(teeth: number[]): Gear[] {
  let x = 0;
  return teeth.map((t, i) => {
    const r = radius(t);
    x += i === 0 ? r : radius(teeth[i - 1]) + r + 1;
    return { x, y: 0, r, teeth: t, label: String.fromCharCode(65 + i) };
  });
}

function gearsDir(r: Rng, lv: number): Made {
  if (lv >= 7 && r() < 0.35) {
    // 세 기어가 서로 맞물린 삼각형 → 꽉 끼어 돌 수 없다
    const t = 16, rr = radius(t), d = 2 * rr + 1;
    const gears: Gear[] = [{ x: 0, y: 0, r: rr, teeth: t, label: 'A' }, { x: d, y: 0, r: rr, teeth: t, label: 'B' }, { x: d / 2, y: (d * Math.sqrt(3)) / 2, r: rr, teeth: t, label: 'C' }];
    return { kind: 'gears', gears, showTeeth: false, shafts: [], driverCW: true, ask: 'A를 시계 방향으로 돌리면 C는 어떻게 되나? (세 기어가 서로 맞물려 있다)', ...dirOptions(r, true, true), n: 3, explain: '맞물린 기어는 서로 반대로 돌아야 하는데, 셋이 고리처럼 맞물리면 A와 C가 같은 방향이면서 반대 방향이어야 해서 꽉 낀다.' };
  }
  const count = lv <= 2 ? 3 : lv <= 4 ? int(r, 4, 5) : int(r, 5, 6);
  const gears = gearRow(Array.from({ length: count }, () => pick(r, TEETH.slice(0, 6))));
  const k = r() < 0.7 ? count - 1 : int(r, 1, count - 2);
  const driverCW = r() < 0.5;
  const cw = k % 2 === 0 ? driverCW : !driverCW;
  return { kind: 'gears', gears, showTeeth: false, shafts: [], driverCW, ask: `A가 ${driverCW ? CW : CCW}으로 돌면 ${gears[k].label}는?`, ...dirOptions(r, cw), n: 2, explain: `맞물린 기어는 서로 반대로 돈다. A에서 ${gears[k].label}까지 ${k}번 건너므로 ${k % 2 === 0 ? '짝수 번 → A와 같은 방향' : '홀수 번 → A와 반대 방향'}.` };
}

function gearsSpeed(r: Rng, lv: number): Made | null {
  if (lv >= 8 && r() < 0.5) {
    // 같은 축에 붙은 두 기어 (B와 C가 함께 돈다)
    const [ta, tb, tc, td] = [pick(r, TEETH), pick(r, TEETH), pick(r, TEETH), pick(r, TEETH)];
    const k = int(r, 1, 6), ans = (k * ta * tc) / (tb * td);
    if (ans * 2 !== Math.round(ans * 2) || ans > 40 || ans < 0.5 || tc >= tb) return null;
    const ra = radius(ta), rb = radius(tb), rc = radius(tc), rd = radius(td);
    const gears: Gear[] = [
      { x: ra, y: 0, r: ra, teeth: ta, label: 'A' },
      { x: ra + ra + rb + 1, y: 0, r: rb, teeth: tb, label: 'B' },
      { x: ra + ra + rb + 1, y: 0, r: rc, teeth: tc, label: 'C' },
      { x: ra + ra + rb + 1 + rc + rd + 1, y: rc + rd > rb ? 0 : 0, r: rd, teeth: td, label: 'D' },
    ];
    return { kind: 'gears', gears, showTeeth: true, shafts: [[1, 2]], driverCW: true, ask: `A가 ${k}바퀴 돌 때 D는 몇 바퀴 도나? (B와 C는 같은 축에 붙어 함께 돈다)`, ...numOptions(r, ans, [(k * ta) / tb, (k * tb * td) / (ta * tc), (k * ta) / td], '바퀴'), n: 4, explain: `B = ${k} × ${ta}/${tb}바퀴, C도 같은 횟수, D = B × ${tc}/${td} = ${fmt(ans)}바퀴` };
  }
  const idler = lv >= 5 && r() < 0.6;
  const ta = pick(r, TEETH), tb = pick(r, TEETH), k = int(r, 1, 8);
  const ans = (k * ta) / tb;
  if (ta === tb || ans * 2 !== Math.round(ans * 2) || ans > 40) return null;
  const teeth = idler ? [ta, pick(r, TEETH), tb] : [ta, tb];
  const gears = gearRow(teeth);
  const last = gears[gears.length - 1];
  last.label = 'B';
  if (idler) gears[1].label = '중간';
  return { kind: 'gears', gears, showTeeth: true, shafts: [], driverCW: true, ask: `A가 ${k}바퀴 돌 때 B는 몇 바퀴 도나?`, ...numOptions(r, ans, [(k * tb) / ta, k, idler ? (k * ta) / teeth[1] : ans + 1], '바퀴'), n: 4, explain: `톱니 수 × 회전수가 같다: ${ta} × ${k} = ${tb} × B → B = ${fmt(ans)}바퀴${idler ? ' (중간 기어의 톱니 수는 상관없다)' : ''}` };
}

// ---- 벨트 ----
function belt(r: Rng, lv: number): Made | null {
  const crossed = r() < 0.5;
  const da = pick(r, [10, 20, 30, 40]), db = pick(r, [10, 20, 30, 40]);
  if (lv >= 4 && da !== db && r() < 0.6) {
    const k = int(r, 1, 6), ans = (k * da) / db;
    if (ans * 2 !== Math.round(ans * 2)) return null;
    return { kind: 'belt', ra: da, rb: db, crossed, showSize: true, ask: `지름 ${da}cm 바퀴 A가 ${k}바퀴 돌 때 지름 ${db}cm 바퀴 B는 몇 바퀴 도나?`, ...numOptions(r, ans, [(k * db) / da, k], '바퀴'), n: 4, explain: `벨트가 움직인 길이가 같다: ${da} × ${k} = ${db} × B → B = ${fmt(ans)}바퀴` };
  }
  return { kind: 'belt', ra: da, rb: db, crossed, showSize: false, ask: `A가 시계 방향으로 돌면 B는?`, ...dirOptions(r, !crossed), n: 2, explain: crossed ? '벨트를 X자로 꼬아 걸면 반대 방향으로 돈다.' : '벨트를 그냥 걸면 같은 방향으로 돈다.' };
}

// ---- 지렛대 ----
function lever(r: Rng, lv: number): Made | null {
  const balanceQ = lv >= 3 && r() < 0.6;
  const leftN = lv >= 7 ? 2 : 1;
  const left = shuffle(r, [1, 2, 3, 4, 5]).slice(0, leftN).map((d) => ({ pos: -d, kg: int(r, 1, 6) * 2 }));
  const torqueL = left.reduce((s, w) => s + -w.pos * w.kg, 0);
  if (balanceQ) {
    const d = int(r, 1, 5), ans = torqueL / d;
    if (!Number.isInteger(ans) || ans > 60) return null;
    const weights = [...left, { pos: d, kg: null }];
    return { kind: 'lever', weights, ask: '수평을 이루려면 오른쪽 ? 에 몇 kg을 올려야 하나? (눈금 간격은 같다)', ...numOptions(r, ans, [left.reduce((s, w) => s + w.kg, 0), torqueL, Math.abs(ans - 2) || ans + 4], 'kg'), n: 4, explain: `무게 × 거리가 같아야 한다: 왼쪽 ${left.map((w) => `${w.kg}×${-w.pos}`).join(' + ')} = ${torqueL} = ? × ${d} → ${ans}kg` };
  }
  const d = int(r, 1, 5), kg = int(r, 1, 8) * 2, torqueR = d * kg;
  const ans = torqueL > torqueR ? 0 : torqueL < torqueR ? 1 : 2;
  const options = ['왼쪽이 내려간다', '오른쪽이 내려간다', '수평을 이룬다'];
  return { kind: 'lever', weights: [...left, { pos: d, kg }], ask: '막대는 어떻게 되나? (막대 무게는 무시, 눈금 간격은 같다)', options, answer: ans, n: 3, explain: `무게 × 거리: 왼쪽 ${torqueL}, 오른쪽 ${kg}×${d} = ${torqueR} → ${options[ans]}` };
}

// ---- 도르래 ----
function pulley(r: Rng, lv: number): Made {
  const ropes = lv <= 4 ? pick(r, [1, 2]) : pick(r, [2, 3, 4]);
  const kg = ropes * int(r, 3, 15) * 2;
  if (lv >= 6 && r() < 0.4) {
    return { kind: 'pulley', ropes, kg, ask: '물체를 1m 올리려면 줄을 몇 m 당겨야 하나?', ...numOptions(r, ropes, [1, 1 / ropes, ropes + 1], 'm'), n: 4, explain: `물체를 받치는 줄이 ${ropes}가닥이면 힘은 1/${ropes}로 줄지만 줄은 ${ropes}배 당겨야 한다 (일의 양은 같다).` };
  }
  return { kind: 'pulley', ropes, kg, ask: `${kg}kg 물체를 천천히 들어 올리는 데 드는 힘은 몇 kg 무게만큼인가? (도르래 무게·마찰 무시)`, ...numOptions(r, kg / ropes, [kg, kg / (ropes + 1), ropes > 1 ? kg / (ropes - 1) : kg * 2], 'kg'), n: 4, explain: `물체(움직도르래)를 받치는 줄이 ${ropes}가닥 → 힘 = ${kg} ÷ ${ropes} = ${fmt(kg / ropes)}kg${ropes === 1 ? ' (고정도르래는 방향만 바꾼다)' : ''}` };
}

// ---- 빗면 · 유압 ----
function incline(r: Rng): Made | null {
  const weight = pick(r, [100, 200, 300, 400, 600]), h = int(r, 1, 3), len = int(r, h + 1, 6);
  const f = (weight * h) / len;
  if (!Number.isInteger(f)) return null;
  return { kind: 'incline', weight, h, len, ask: `무게 ${weight}N 인 상자를 빗면을 따라 끌어올릴 때 드는 힘은? (마찰 무시)`, ...numOptions(r, f, [weight, (weight * len) / h, weight / h], 'N'), n: 4, explain: `일의 양은 같다: 힘 × ${len}m = ${weight}N × ${h}m → 힘 = ${fmt(f)}N` };
}

function hydraulic(r: Rng): Made | null {
  const small = pick(r, [2, 4, 5, 10]), big = small * pick(r, [5, 10, 20]), force = pick(r, [10, 20, 30, 50]);
  const ans = (force * big) / small;
  return { kind: 'hydraulic', small, big, force, ask: `작은 피스톤(넓이 ${small}cm²)을 ${force}N 으로 누르면 큰 피스톤(넓이 ${big}cm²)이 밀어 올리는 힘은?`, ...numOptions(r, ans, [force, (force * small) / big, force * (big - small)], 'N'), n: 4, explain: `압력(힘 ÷ 넓이)이 같다: ${force} ÷ ${small} = 큰 힘 ÷ ${big} → ${fmt(ans)}N (파스칼 원리)` };
}

// ---- 운동 원리 (개념) ----
const CONCEPTS: [number, string, string, string[], string][] = [
  [1, '같은 높이에서 쇠공과 같은 크기의 나무공을 동시에 떨어뜨리면? (공기 저항 무시)', '동시에 떨어진다', ['쇠공이 먼저 떨어진다', '나무공이 먼저 떨어진다', '무게에 따라 다르다'], '공기 저항이 없으면 무게와 관계없이 같은 가속도로 떨어진다.'],
  [1, '달리던 버스가 갑자기 멈추면 서 있던 승객의 몸은?', '앞으로 쏠린다', ['뒤로 쏠린다', '그대로 있다', '위로 뜬다'], '관성: 움직이던 몸은 계속 움직이려 한다.'],
  [1, '시소에서 무거운 사람과 가벼운 사람이 균형을 맞추려면 무거운 사람은?', '받침점에 더 가까이 앉는다', ['받침점에서 더 멀리 앉는다', '가벼운 사람과 같은 거리에 앉는다', '어디에 앉아도 같다'], '무게 × 거리가 같아야 하므로 무거운 쪽이 거리를 줄인다.'],
  [1, '스패너로 꽉 조인 볼트를 풀 때 힘이 가장 적게 드는 방법은?', '손잡이 끝을 잡고 돌린다', ['볼트 가까이를 잡고 돌린다', '손잡이 가운데를 잡는다', '어디를 잡아도 같다'], '돌림힘 = 힘 × 거리 → 멀리 잡을수록 적은 힘으로 된다.'],
  [1, '물체의 무게중심이 낮을수록?', '잘 넘어지지 않는다', ['잘 넘어진다', '무거워진다', '아무 차이가 없다'], '무게중심이 낮고 바닥이 넓을수록 안정하다.'],
  [2, '물이 흐르는 관이 좁아지는 곳에서 물의 속도는?', '빨라진다', ['느려진다', '그대로다', '멈춘다'], '같은 시간에 같은 양이 지나가야 하므로 좁은 곳에서는 빨라진다.'],
  [2, '같은 전구 두 개를 같은 전지에 연결할 때 더 밝게 켜지는 연결은?', '병렬 연결', ['직렬 연결', '둘 다 같다', '전구 하나만 켜진다'], '병렬은 각 전구에 전지의 전압이 그대로 걸리고, 직렬은 나눠 걸린다.'],
  [2, '자전거에서 앞은 큰 톱니, 뒤는 작은 톱니로 하면?', '페달 한 바퀴에 바퀴가 더 많이 돈다', ['페달 한 바퀴에 바퀴가 덜 돈다', '페달을 밟는 힘이 덜 든다', '아무 차이가 없다'], '톱니 수 비만큼 뒷바퀴가 더 돈다. 대신 페달을 밟는 힘은 더 든다.'],
  [2, '철길 레일 이음매에 틈을 두는 까닭은?', '더울 때 레일이 늘어나기 때문에', ['빗물이 빠지게 하려고', '소리를 줄이려고', '레일을 가볍게 하려고'], '열팽창: 온도가 오르면 금속이 늘어난다.'],
  [2, '같은 부피의 쇠와 나무를 물속에 완전히 잠기게 했을 때 받는 부력은?', '같다', ['쇠가 더 크다', '나무가 더 크다', '쇠는 부력을 받지 않는다'], '부력은 밀어낸 물의 무게 → 잠긴 부피가 같으면 같다.'],
  [2, '열기구가 떠오르는 까닭은?', '데운 공기가 바깥 공기보다 가볍기 때문에', ['풍선 속 공기가 무거워지기 때문에', '불꽃이 위로 밀어 올리기 때문에', '바람이 들어 올리기 때문에'], '데워진 공기는 밀도가 작아 같은 부피일 때 더 가볍다.'],
  [3, '도끼날을 얇게 갈수록 나무가 잘 쪼개지는 까닭은?', '같은 힘이 좁은 면적에 모여 압력이 커지기 때문에', ['도끼가 가벼워지기 때문에', '마찰이 커지기 때문에', '날이 차가워지기 때문에'], '압력 = 힘 ÷ 넓이.'],
  [3, '높은 곳에 있는 물탱크일수록 수도꼭지에서 물이 세게 나오는 까닭은?', '높이가 높을수록 수압이 커지기 때문에', ['물탱크가 클수록 물이 빠르기 때문에', '공기가 물을 밀기 때문에', '관이 길수록 물이 빨라지기 때문에'], '물의 압력은 수면에서 내려간 깊이(높이 차)에 비례한다.'],
  [3, '회전하는 놀이기구에서 바깥쪽과 안쪽에 앉은 사람 중 더 빨리 움직이는 사람은?', '바깥쪽', ['안쪽', '같다', '그때그때 다르다'], '같은 시간에 한 바퀴를 돌지만 바깥쪽이 더 긴 원을 돈다.'],
  [3, '빙판에서 같은 힘으로 상자를 밀면 아스팔트 위에서보다?', '더 멀리 미끄러진다', ['덜 미끄러진다', '똑같이 간다', '움직이지 않는다'], '얼음은 마찰력이 작아 속도가 덜 줄어든다.'],
  [3, '드라이버 손잡이가 굵을수록?', '적은 힘으로 나사를 돌릴 수 있다', ['더 큰 힘이 든다', '나사가 빨리 빠진다', '아무 차이가 없다'], '바퀴와 축의 원리: 굵은 손잡이를 돌리면 가는 날에 큰 돌림힘이 걸린다.'],
];

function concept(r: Rng, lv: number): Made {
  const maxD = lv <= 3 ? 1 : lv <= 6 ? 2 : 3;
  const [, q, right, wrong, why] = pick(r, CONCEPTS.filter((c) => c[0] <= maxD));
  const options = shuffle(r, [right, ...wrong]);
  return { kind: 'concept', ask: q, options, answer: options.indexOf(right), n: 4, explain: why };
}

type Maker = (r: Rng, lv: number) => Made | null;
const PLAN: Record<string, Maker[]> = {
  low: [gearsDir, gearsDir, lever, belt, concept, concept],
  mid: [gearsDir, gearsSpeed, lever, pulley, belt, concept],
  high: [gearsSpeed, belt, pulley, (r) => incline(r), lever, concept],
  top: [gearsDir, gearsSpeed, pulley, (r) => hydraulic(r), lever, concept],
};

export function makeMechRound(level: number, seed: number): MechQuestion[] {
  const rng = makeRng(seed);
  const lv = Math.min(10, Math.max(1, level));
  const plan = PLAN[lv <= 2 ? 'low' : lv <= 4 ? 'mid' : lv <= 6 ? 'high' : 'top'];
  const limitSec = 30 + lv * 3;
  const out: MechQuestion[] = [];
  const asked = new Set<string>();
  for (const make of shuffle(rng, plan)) {
    for (let guard = 0; guard < 100; guard++) {
      const m = make(rng, lv);
      if (!m || m.answer < 0 || (m.kind === 'concept' && asked.has(m.ask))) continue; // 같은 개념 문제만 겹치지 않게 (그림 문제는 질문 문장이 같아도 그림이 다르다)
      asked.add(m.ask);
      out.push({ ...m, limitSec } as MechQuestion);
      break;
    }
  }
  return out;
}
